// frontend/src/pages/Home.tsx
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  Button,
  Divider,
  Fade
} from '@mui/material';
import { 
  Folder as FolderIcon,
  Search as SearchIcon,
  AccessTime as RecentIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * Home page component showing available application features
 */
const Home: React.FC = () => {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  const features = [
    {
      title: 'Latest Uploads',
      description: 'View and play the 10 most recently completed experiments.',
      icon: <RecentIcon fontSize="large" color="primary" />,
      action: () => navigate('/recent-files')
    },
    {
      title: 'Browse Files',
      description: 'Browse all files in Open Bay Data Hub.',
      icon: <FileIcon fontSize="large" color="primary" />,
      action: () => navigate('/browse')
    },
    {
      title: 'Browse Folders',
      description: 'Browse files organized in folders by experiment.',
      icon: <FolderIcon fontSize="large" color="primary" />,
      action: () => navigate('/folders')
    },
    {
      title: 'Search Files',
      description: 'Search for specific files by name or keywords.',
      icon: <SearchIcon fontSize="large" color="primary" />,
      action: () => navigate('/search')
    }
  ];

  return (
    <Fade in={loaded} timeout={800}>
      <Box>
        <Paper elevation={0} sx={{ 
          p: 3, 
          mb: 4, 
          backgroundColor: 'primary.light',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
          }
        }}>
          <Typography variant="h3" component="h1" gutterBottom color="white">
            Welcome to Open Bay Data Hub
          </Typography>
          <Typography variant="h6" component="h2" color="white">
            Experiments are organized by your Accession ID
          </Typography>
        </Paper>

        <Grid container spacing={3} className="staggered-fade-in">
          {features.map((feature, index) => (
            <Grid item xs={12} md={index === 3 ? 12 : 4} key={feature.title}>
              <Card 
                className="hover-card"
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 20px rgba(11, 65, 205, 0.15)', 
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    mb: 2,
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}>
                    {feature.icon}
                  </Box>
                  <Typography gutterBottom variant="h5" component="h2" align="center">
                    {feature.title}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body1">
                    {feature.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    fullWidth 
                    variant="contained" 
                    onClick={feature.action}
                    className="hover-button ripple"
                    sx={{
                      transition: 'all 0.25s ease',
                      boxShadow: '0 2px 5px rgba(11, 65, 205, 0.1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(11, 65, 205, 0.2)',
                      }
                    }}
                  >
                    Go to {feature.title}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        {/* Add test button here for external link verification */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="outlined"
            color="primary"
            component="a"
            href="https://docs.google.com/forms/d/e/1FAIpQLSdrb4mYtyjdHDjCEqNnGX-sDw-UJuXs7geaZ9OX0v-eecbhtw/viewform?usp=dialog"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              transition: 'all 0.25s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(11, 65, 205, 0.2)',
              }
            }}
          >
            Report a Bug
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default Home;